import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Card } from '../../components/Card'
import { SectionHead } from '../../components/SectionHead'
import { Badge } from '../../components/StatusBadge'
import { IS_DEMO_MODE } from '../../lib/demoMode'
import { useBudgetMonitor } from '../../api/metrics'
import {
  useApplyEvent,
  useApproveEvent,
  useBatchDecision,
  useInbox,
  useInboxSummary,
  usePromoteDraft,
  useRecentSyncRuns,
  useRejectEvent,
  useSyncSafetyState,
  type InboxStatus,
} from '../../api/inbox'
import { failedSyncRuns, stalledSyncRuns, reconciledStaleSyncRuns, safetyBlockText, syncRunSeverity } from './alerts'
import { groupInboxItems, isActionable } from './groupItems'
import { InboxCard } from './InboxCard'

function eur(n: number): string {
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}

const TABS: ReadonlyArray<readonly [InboxStatus, string]> = [
  ['pending', 'Offen'],
  ['draft', 'Drafts'],
  ['approved', 'Freigegeben'],
  // Spine-3-Integration (Cross-Check #4): eigener Tab fuer pruefbeduerftige
  // Apply-Versuche mit unbekanntem Amazon-Ausgang (Status 'reconcile_required',
  // Fenster I) — sonst kann der Operator sie nicht sehen/aufloesen.
  ['reconcile_required', 'Prüfen'],
]

export function InboxPage() {
  const [tab, setTab] = useState<InboxStatus>('pending')
  const summaryQ = useInboxSummary()
  const listQ = useInbox(tab)
  const budgetQ = useBudgetMonitor()
  const syncQ = useRecentSyncRuns()
  // GLB-8: der server-seitige Safety-State ist ab jetzt die Grundlage der
  // Sperre. Er kennt keinen Ausschnitt — anders als `syncQ` (Deckel 100).
  const safetyQ = useSyncSafetyState()

  const approve = useApproveEvent()
  const reject = useRejectEvent()
  const apply = useApplyEvent()
  const promote = usePromoteDraft()
  const batch = useBatchDecision()

  const summary = summaryQ.data?.summary
  const items = listQ.data?.items ?? []
  const total = listQ.data?.total ?? 0
  const groups = groupInboxItems(items)
  const budgetAlerts = (budgetQ.data?.alerts ?? []).filter((a) => a.alertLevel !== 'ok')
  const syncRuns = syncQ.data?.runs ?? []
  // Vom Backend-Reconciler als verwaist markierte (failed) Runs blockieren weiter
  // (fail-closed), bis ein neuerer erfolgreicher Sync denselben Key ueberschreibt —
  // sonst wuerde das Reconcile einen harten Block still in einen bloszen Alert wandeln.
  const reconciledStaleRuns = reconciledStaleSyncRuns(syncRuns)
  const reconciledIds = new Set(reconciledStaleRuns.map((r) => r.id))
  // Reconcilte Runs NICHT zusaetzlich als reinen failed-Alert doppeln.
  const failedRuns = failedSyncRuns(syncRuns).filter((r) => !reconciledIds.has(r.id))
  // Haengende running-Runs (Fire-and-forget-Worker nach Prozessabbruch) und ein
  // komplett leerer Sync-Verlauf duerfen die Inbox nicht "gesund" aussehen lassen.
  const stalledRuns = stalledSyncRuns(syncRuns)
  const syncHistoryEmpty = syncQ.isSuccess && syncRuns.length === 0
  // GLB-8: vollstaendiger Server-Befund. `serverBlocks` kann Faelle enthalten,
  // die in `syncRuns` fehlen (Lauf jenseits der neuesten 100) — deshalb hat er
  // seine eigene Alert-Karte, sonst sperrte die Ansicht ohne Begruendung.
  const serverBlocks = safetyQ.data?.blocked ?? []
  const serverHistoryEmpty = safetyQ.data?.historyEmpty ?? false
  const serverBlocked = serverBlocks.length > 0 || serverHistoryEmpty
  // Faellt die Auskunft AUS (503, Netz, Timeout), ist der Zustand UNBEKANNT —
  // gesperrt wird trotzdem (fail-closed), aber mit eigenem Grund. Die Seite muss
  // dabei benutzbar bleiben: sie rendert weiter, nur die Entscheidungen sind zu.
  const safetyUnknown = safetyQ.isError
  const failed = summary?.failedApplies ?? 0
  // Spine-3-Integration (Cross-Check #2/#4): pruefbeduerftige Apply-Versuche
  // (reconcile_required) — eigenes Signal, getrennt von failedApplies.
  const reconcileRequired = summary?.reconcileRequiredCount ?? 0
  // CLEANUP-INBOX (Scheibe N): offene Pruefmarker des 0-Impression-Cleanups.
  // EIGENER Zaehler, KEINE Addition auf reconcileRequired: der beschriftet den Tab
  // „Prüfen", und der listet promotion_events — ein Cleanup-Fall taucht dort weder
  // auf noch liesse er sich dort aufloesen (das geschieht per Quittierung in der
  // Cleanup-Ansicht). Eine Summe stuende ueber einer kuerzeren Liste.
  const cleanupReconcile = summary?.cleanupReconcileCount ?? 0
  // Query-Fehler nutzen NICHT den globalen Mutations-Toast — sonst wirkt die Inbox
  // gesund, obwohl Sicherheitssignale (Summary/Budget/Sync) gar nicht geladen sind.
  const signalsUnavailable =
    summaryQ.isError || budgetQ.isError || syncQ.isError || safetyQ.isError || syncHistoryEmpty
  // `cleanupReconcile` gehoert HIER hinein (Scheibe N): sonst bliebe die Kachel
  // unsichtbar, wenn sie das einzige Signal ist — genau der Fall, um den es geht.
  const hasAlerts = budgetAlerts.length > 0 || failed > 0 || reconcileRequired > 0 || cleanupReconcile > 0 || failedRuns.length > 0 || stalledRuns.length > 0 || reconciledStaleRuns.length > 0 || serverBlocked || signalsUnavailable
  const busy = approve.isPending || reject.isPending || apply.isPending || promote.isPending || batch.isPending
  // Fail-closed: Keine Entscheidung freigeben, solange die Sicherheitssignale
  // (Summary/Budget/Sync) noch laden ODER mit Fehler enden — sonst wirkt die Queue
  // "sauber", bevor Warnungen da sind bzw. obwohl sie gar nicht geladen werden konnten.
  // Haengende Syncs blockieren ebenfalls: die Metriken koennten veraltet sein.
  const safetyLoading = summaryQ.isLoading || budgetQ.isLoading || syncQ.isLoading || safetyQ.isLoading
  // GLB-8: `serverBlocked` ist ADDITIV zu den bisherigen Client-Kriterien. Die
  // Oberflaeche wird dadurch nie WENIGER sperrend als vorher; sie sperrt jetzt
  // zusaetzlich in den Faellen, die das 100er-Sample nicht zeigen konnte. Die
  // Durchsetzung liegt ohnehin nicht mehr hier, sondern am Server-Guard
  // (`shared/middleware/sync-freshness-guard.ts`, 409 `SYNC_STALE`).
  const decisionsBlocked = busy || safetyLoading || signalsUnavailable || serverBlocked || stalledRuns.length > 0 || reconciledStaleRuns.length > 0
  const truncated = Math.max(0, total - items.length)

  const tabCount = (id: InboxStatus): string => {
    if (id === 'pending') return ` (${summary?.pendingCount ?? 0})`
    if (id === 'draft') return ` (${summary?.draftCount ?? 0})`
    if (id === 'reconcile_required') return reconcileRequired > 0 ? ` (${reconcileRequired})` : ''
    return ''
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-[22px] font-bold tracking-tight text-[var(--foreground)]">Freigaben</h1>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Deine Morgenroutine — Entscheidungen nach Priorität, das teuerste zuerst.
        </p>
      </div>

      {IS_DEMO_MODE && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <strong className="font-bold">Demo-Modus:</strong> Freigeben, Ablehnen, Anwenden und
          Übernehmen sind deaktiviert — das Backend blockt jeden Schreibpfad (423). Nur Ansicht.
        </div>
      )}

      {/* Zone 1: Alerts */}
      {hasAlerts && (
        <section>
          <SectionHead title="Achtung" subtitle="Zuerst prüfen" />
          <div className="grid gap-2 sm:grid-cols-2">
            {failed > 0 && (
              <Card className="flex items-start gap-2 border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="text-sm font-semibold text-red-800">{failed} fehlgeschlagene Applies</p>
                  <p className="text-xs text-red-700">Details im Tab „Freigegeben".</p>
                </div>
              </Card>
            )}
            {reconcileRequired > 0 && (
              <Card className="flex items-start gap-2 border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="text-sm font-semibold text-red-800">{reconcileRequired} Applies prüfbedürftig</p>
                  <p className="text-xs text-red-700">
                    Unklarer Amazon-Ausgang — im Tab „Prüfen" den Ist-Zustand prüfen und auflösen.
                  </p>
                </div>
              </Card>
            )}
            {/* CLEANUP-INBOX (Scheibe N): zweite Pruefart, eigener Weg.
                Der Cleanup-Pruefmarker liegt in `cleanup_events` und wird in der
                Cleanup-Ansicht quittiert — deshalb ein Link dorthin statt eines
                Verweises auf den Tab „Prüfen", der promotion_events listet.
                Die Zahl ist der BESTAND; die Zielansicht fuehrt dieselbe Zahl in
                ihrer Ueberschrift und sagt selbst, wenn ihre Liste kuerzer ist. */}
            {cleanupReconcile > 0 && (
              <Card data-testid="cleanup-reconcile-kachel" className="flex items-start gap-2 border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0">
                  <p data-testid="cleanup-reconcile-titel" className="text-sm font-semibold text-red-800">
                    {cleanupReconcile} Cleanup-Fälle prüfbedürftig
                  </p>
                  {/* Zweitreview-Fund (Low): „quittieren" ist im Demo-Modus nicht
                      möglich — die Quittier-Route liegt hinter requireWriteMode und
                      antwortet 423. Der Text darf keine Handlung versprechen, die die
                      Plattform gerade verweigert; ansehen kann der Operator den Fall
                      trotzdem. Der Demo-Banner oben nennt „Quittieren" nicht mit. */}
                  <p data-testid="cleanup-reconcile-text" className="text-xs text-red-700">
                    {IS_DEMO_MODE
                      ? 'Amazon-Ausgang beim Archivieren ungeklärt — in der Cleanup-Ansicht einsehbar; Quittieren ist im Demo-Modus deaktiviert.'
                      : 'Amazon-Ausgang beim Archivieren ungeklärt — in der Cleanup-Ansicht prüfen und quittieren.'}
                  </p>
                  <Link
                    to="/promotions?tab=cleanup"
                    className="mt-1 inline-block text-xs font-medium text-red-800 underline"
                  >
                    Zur Cleanup-Ansicht
                  </Link>
                </div>
              </Card>
            )}
            {budgetAlerts.map((a) => (
              <Card key={a.campaignId} className="flex items-start gap-2 border-amber-200 bg-amber-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-amber-900">{a.name}</p>
                  <p className="text-xs text-amber-800">
                    Budget-Auslastung {a.utilization.toFixed(0)}% ({a.alertLevel === 'critical' ? 'kritisch' : 'Warnung'})
                  </p>
                </div>
              </Card>
            ))}
            {failedRuns.map((r) => {
              const isFail = syncRunSeverity(r) === 'failed'
              return (
                <Card
                  key={`sync-${r.id}`}
                  className={`flex items-start gap-2 p-3 ${isFail ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
                >
                  <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${isFail ? 'text-red-600' : 'text-amber-600'}`} />
                  <div className="min-w-0">
                    <p className={`truncate text-sm font-semibold ${isFail ? 'text-red-800' : 'text-amber-900'}`}>
                      {isFail ? 'Sync fehlgeschlagen' : 'Sync mit Warnungen'}: {r.syncType} · Profil {r.profileId}
                    </p>
                    <p className={`truncate text-xs ${isFail ? 'text-red-700' : 'text-amber-800'}`}>
                      {r.errors?.[0] ?? 'Datenaufnahme unvollständig — Zahlen könnten veraltet sein.'}
                    </p>
                  </div>
                </Card>
              )
            })}
            {stalledRuns.map((r) => (
              <Card key={`stalled-${r.id}`} className="flex items-start gap-2 border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-red-800">
                    Sync hängt: {r.syncType} · Profil {r.profileId}
                  </p>
                  <p className="text-xs text-red-700">
                    Läuft seit über 30 Minuten ohne Abschluss — Metriken könnten veraltet sein. Entscheidungen sind gesperrt.
                  </p>
                </div>
              </Card>
            ))}
            {reconciledStaleRuns.map((r) => (
              <Card key={`reconciled-${r.id}`} className="flex items-start gap-2 border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-red-800">
                    Sync abgebrochen: {r.syncType} · Profil {r.profileId}
                  </p>
                  <p className="text-xs text-red-700">
                    Ein verwaister Lauf wurde als fehlgeschlagen bereinigt — die letzte Datenaufnahme war nicht erfolgreich, Metriken könnten veraltet sein. Entscheidungen bleiben gesperrt, bis ein neuer Sync erfolgreich durchläuft.
                  </p>
                </div>
              </Card>
            ))}
            {/* GLB-8: die SERVER-Sperre, mit Grund. Diese Karte ist nicht die
                Doppelung der beiden Karten darueber: jene entstehen aus dem
                100er-Sample und koennen den blockierenden Lauf schlicht nicht
                enthalten. Ohne diese Karte waeren in genau diesen Faellen alle
                Knoepfe grau und die Ansicht schwieg zum Grund. */}
            {(serverBlocked || safetyUnknown) && (
              <Card
                data-testid="sync-safety-gate-kachel"
                className="flex items-start gap-2 border-red-200 bg-red-50 p-3 sm:col-span-2"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-800">
                    {safetyUnknown
                      ? 'Sync-Zustand nicht prüfbar — Entscheidungen gesperrt'
                      : 'Datenaufnahme nicht frisch — Entscheidungen gesperrt'}
                  </p>
                  <p data-testid="sync-safety-gate-grund" className="text-xs text-red-700">
                    {safetyUnknown
                      ? 'Der Sync-Zustand ließ sich nicht abrufen — im Zweifel gesperrt. Die übrige Ansicht bleibt nutzbar; beim nächsten Abruf löst sich das von selbst, wenn das Backend wieder antwortet.'
                      : serverHistoryEmpty
                        ? 'Für kein aktives Profil liegt ein sauber abgeschlossener Sync-Lauf vor — ohne Datenaufnahme keine belastbare Entscheidungsgrundlage.'
                        : serverBlocks.map(safetyBlockText).join(' · ')}
                  </p>
                  <p className="mt-1 text-xs text-red-700">
                    {safetyUnknown
                      ? 'Das Backend entscheidet in diesem Fall ebenfalls im Zweifel gegen die Ausführung (503).'
                      : 'Das Backend weist Freigaben, Anwendungen und Batch-Aktionen für die betroffenen Profile ab (409); ein offener Abbruch-Block löst sich erst mit einem sauber durchgelaufenen Sync.'}
                  </p>
                  <Link to="/sync" className="mt-1 inline-block text-xs font-medium text-red-800 underline">
                    Zur Sync-Ansicht
                  </Link>
                </div>
              </Card>
            )}
            {signalsUnavailable && (
              <Card className="flex items-start gap-2 border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-800">Sicherheitssignale nicht verfügbar</p>
                  <p className="text-xs text-red-700">
                    {syncHistoryEmpty
                      ? 'Keine Sync-Runs vorhanden — ohne Datenaufnahme keine belastbare Entscheidungsgrundlage.'
                      : 'Zusammenfassung, Budget-Monitor oder Sync-Status ließen sich nicht laden — diese Ansicht ist unvollständig.'}
                  </p>
                </div>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Zone 2: Entscheidungs-Queue */}
      <section>
        <SectionHead
          title="Entscheidungs-Queue"
          subtitle="Freigeben, ablehnen oder anwenden"
          right={
            <div className="flex gap-1">
              {TABS.map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`rounded-md px-3 py-1 text-xs font-medium ${
                    tab === id ? 'bg-zinc-900 text-white' : 'border border-[var(--border)] text-[var(--foreground)]'
                  }`}
                >
                  {label}{tabCount(id)}
                </button>
              ))}
            </div>
          }
        />

        {listQ.isLoading ? (
          <Card className="p-6 text-center text-sm text-[var(--muted-foreground)]">Lädt…</Card>
        ) : listQ.isError ? (
          <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Inbox konnte nicht geladen werden. Bitte später erneut versuchen.
          </Card>
        ) : groups.length === 0 ? (
          <Card className="flex flex-col items-center gap-2 p-8 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <p className="text-sm font-semibold text-[var(--foreground)]">Nichts zu entscheiden — alles erledigt.</p>
            <Link to="/performance" className="text-xs font-medium text-indigo-600 hover:underline">
              Zur Performance-Übersicht
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.groupKey}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[13px] font-bold text-[var(--foreground)]">{g.label}</h3>
                    <Badge tone="neutral">{g.items.length}</Badge>
                    <span className="text-xs text-[var(--muted-foreground)]">Σ {eur(g.totalImpact)}</span>
                  </div>
                  {tab === 'pending' && !g.groupKey.endsWith(':other') && g.items.every(isActionable) && (
                    <button
                      onClick={() => batch.mutate({ action: 'approve', ids: g.items.map((i) => i.id) })}
                      disabled={decisionsBlocked || IS_DEMO_MODE}
                      title={IS_DEMO_MODE ? 'Im Demo-Modus deaktiviert (kein Schreibzugriff)' : 'Ganze Gruppe freigeben'}
                      className="rounded-md border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-[var(--foreground)] disabled:opacity-50"
                    >
                      Gruppe freigeben
                    </button>
                  )}
                </div>
                <div className="grid gap-2 lg:grid-cols-2">
                  {g.items.map((it) => (
                    <InboxCard
                      key={it.id}
                      item={it}
                      isDemoMode={IS_DEMO_MODE}
                      busy={decisionsBlocked}
                      onApprove={() => approve.mutate({ id: it.id })}
                      onReject={(note) => reject.mutateAsync({ id: it.id, note })}
                      onApply={() => apply.mutate({ id: it.id })}
                      onPromoteDraft={() => promote.mutate({ id: it.id })}
                    />
                  ))}
                </div>
              </div>
            ))}
            {truncated > 0 && (
              <p className="pt-1 text-center text-xs text-[var(--muted-foreground)]">
                Es werden die {items.length} Einträge mit dem höchsten Spend gezeigt — {truncated} weitere
                in diesem Tab. Gruppen-Summen und „Gruppe freigeben" beziehen sich nur auf die sichtbaren.
              </p>
            )}
          </div>
        )}
      </section>

      {/* Zone 3: Tagesabschluss */}
      <section>
        <SectionHead title="Tagesabschluss" />
        <Card className="flex items-center gap-3 p-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <div>
            <p className="text-sm font-semibold text-[var(--foreground)]">
              Gestern angewendet: {summary?.appliedYesterday ?? 0}
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">Änderungen, die gestern bei Amazon aktiv wurden.</p>
          </div>
        </Card>
      </section>
    </div>
  )
}

export default InboxPage
